import { Box, Grid, RadioGroup, Radio, FormControlLabel, MenuItem, Select, Tooltip } from "@mui/material";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs, { Dayjs } from "dayjs";
import { useState, useCallback, useEffect, useRef } from "react";
import CustomDatePicker, { DateObject } from "react-multi-date-picker";
import { getPreviousWeekday, getNextWeekday } from "../../../helpers";
import { DateSelectionMode } from "../../../constants";

/**
 * The "Bucket Inception" option (Strategy Report only). `range` is null while
 * it is loading or when none exists; `disabledReason` is the tooltip shown on
 * the disabled option when none exists (null while loading).
 */
export interface BucketInceptionOption {
  range: [string, string] | null;
  disabledReason: string | null;
}

interface DateSelectorProps {
  onDateChange?: (range: [string | null, string | null]) => void;
  defaultRange?: [string | null, string | null];
  style?: React.CSSProperties;
  additionalComponents?: React.ReactNode | null;
  inceptionDate?: string | null;
  removedDate?: string | null;
  bucketInception?: BucketInceptionOption;
  /**
   * Called with the Bucket Inception range when that option is picked, and
   * again whenever the range changes while it stays picked — its own channel
   * rather than `onDateChange`, because the report replaces every other
   * period with it instead of adding it. Keep its identity stable: it is an
   * effect dependency.
   */
  onBucketInceptionSelect?: (range: [string, string]) => void;
  /**
   * The option to start on (the Strategy Report's remembered pick); Year when
   * absent. A remembered Bucket Inception keeps the period the report already
   * holds: its range is not re-applied, and a range that is not there yet
   * does not reset it to Year, until the reader next touches this control or
   * `releaseRestoredPick` turns true.
   */
  defaultMode?: DateSelectionMode;
  /**
   * The report switched to another account or Bucket 3 after the page loaded:
   * the stored period no longer belongs to the option, so a remembered pick
   * behaves as a picked one again (takes the new range, or falls back to Year).
   */
  releaseRestoredPick?: boolean;
  /** Called with the option every time it changes, so the report can remember it. */
  onModeChange?: (mode: DateSelectionMode) => void;
  /**
   * How the options are offered: a radio per option (default), or one
   * drop-down beside the date picker with the picked period(s) on the line
   * below — the Strategy Report's compact layout. `style` applies to the
   * radios only.
   */
  variant?: "radios" | "dropdown";
}

const MODE_LABELS: Record<DateSelectionMode, string> = {
  [DateSelectionMode.YEAR]: "Year",
  [DateSelectionMode.MONTH]: "Month",
  [DateSelectionMode.RANGE]: "Range",
  [DateSelectionMode.BUCKET_INCEPTION]: "Bucket Inception",
  [DateSelectionMode.INCEPTION]: "Since Inception",
};

const DEFAULT_RANGE: [string | null, string | null] = [null, null];

const formatRange = ([startDate, endDate]: [string, string]) =>
  `${dayjs(startDate).format("MM/DD/YYYY")} - ${dayjs(endDate).format("MM/DD/YYYY")}`;

const DateSelector = ({ defaultRange, onDateChange, style, additionalComponents, inceptionDate, removedDate, bucketInception, onBucketInceptionSelect, defaultMode, onModeChange, releaseRestoredPick, variant = "radios" }: DateSelectorProps) => {
  const [selectionMode, setSelectionMode] = useState<DateSelectionMode>(defaultMode ?? DateSelectionMode.YEAR);
  const [range, setRange] = useState<[string | null, string | null]>(DEFAULT_RANGE);
  // A remembered Bucket Inception pick, not yet touched since the page opened:
  // the range still loading in (and the account behind it) must not replace
  // the stored period or drop the pick.
  const restoredBucketInceptionRef = useRef(defaultMode === DateSelectionMode.BUCKET_INCEPTION);
  if (releaseRestoredPick) restoredBucketInceptionRef.current = false;

  useEffect(() => {
    onModeChange?.(selectionMode);
  }, [selectionMode, onModeChange]);

  // The option's range can vanish while it is picked (another account or
  // Bucket 3): leave nothing checked-but-disabled with no picker beside it.
  // A range that is only reloading (no reason yet) keeps the pick, so the new
  // range can take its place once it arrives.
  const bucketInceptionUnavailable =
    !!bucketInception && !bucketInception.range && bucketInception.disabledReason !== null;
  useEffect(() => {
    if (bucketInceptionUnavailable && !restoredBucketInceptionRef.current) {
      setSelectionMode((mode) =>
        mode === DateSelectionMode.BUCKET_INCEPTION ? DateSelectionMode.YEAR : mode,
      );
    }
  }, [bucketInceptionUnavailable, releaseRestoredPick]);

  // Picking the option — and any change of its range while it stays picked —
  // hands the range over, so the report always matches the option's label.
  const bucketInceptionStart = bucketInception?.range?.[0];
  const bucketInceptionEnd = bucketInception?.range?.[1];
  useEffect(() => {
    if (restoredBucketInceptionRef.current) return;
    if (
      selectionMode === DateSelectionMode.BUCKET_INCEPTION &&
      bucketInceptionStart &&
      bucketInceptionEnd
    ) {
      onBucketInceptionSelect?.([bucketInceptionStart, bucketInceptionEnd]);
    }
  }, [selectionMode, bucketInceptionStart, bucketInceptionEnd, onBucketInceptionSelect, releaseRestoredPick]);

  const handleModeChange = useCallback((mode: DateSelectionMode) => {
    restoredBucketInceptionRef.current = false;
    setSelectionMode(mode);
    if (mode === DateSelectionMode.INCEPTION && inceptionDate) {
      const endDate = removedDate
        ? removedDate
        : getPreviousWeekday(dayjs().format("YYYY-MM-DD")).format("YYYY-MM-DD");
      onDateChange?.([inceptionDate, endDate]);
    }
  }, [inceptionDate, removedDate, onDateChange]);

  const handleBucketInceptionReclick = useCallback(() => {
    restoredBucketInceptionRef.current = false;
    if (
      selectionMode === DateSelectionMode.BUCKET_INCEPTION &&
      bucketInceptionStart &&
      bucketInceptionEnd
    ) {
      onBucketInceptionSelect?.([bucketInceptionStart, bucketInceptionEnd]);
    }
  }, [selectionMode, bucketInceptionStart, bucketInceptionEnd, onBucketInceptionSelect]);

  const handleYearChange = useCallback((value: Dayjs | null) => {
    if (!value) {
      setRange(DEFAULT_RANGE);
      onDateChange?.(DEFAULT_RANGE);
      return;
    }

    const startDate = value.format("YYYY") + "-01-01";
    let endDate = value.format("YYYY") + "-12-31";
    if (value.format("YYYY") === dayjs().year().toString()) {
      endDate = dayjs().format("YYYY-MM-DD");
    }
    const adjStartDate = getNextWeekday(startDate);
    const adjEndDate = getPreviousWeekday(endDate);
    const newRange: [string, string] = [adjStartDate.format("YYYY-MM-DD"), adjEndDate.format("YYYY-MM-DD")];
    setRange(newRange);
    onDateChange?.(newRange);
    return;
  }, [onDateChange]);

  const handleMonthChange = useCallback((value: Dayjs | null) => {
    if (!value) {
      setRange(DEFAULT_RANGE);
      onDateChange?.(DEFAULT_RANGE);
      return;
    }

    const startDate = value.format("YYYY-MM") + "-01";
    let endDate = value.endOf('month').format("YYYY-MM-DD");
    if (value.format("YYYY-MM") === dayjs().format("YYYY-MM")) {
      endDate = dayjs().format("YYYY-MM-DD");
    }
    const adjStartDate = getNextWeekday(startDate);
    const adjEndDate = getPreviousWeekday(endDate);
    const newRange: [string, string] = [adjStartDate.format("YYYY-MM-DD"), adjEndDate.format("YYYY-MM-DD")];
    setRange(newRange);
    onDateChange?.(newRange);
    return;
  }, [onDateChange]);

  const handleRangeChange = useCallback((dates: DateObject[]) => {
    if (dates.length === 2) {
      const [startDate, endDate] = dates;
      const adjStartDate = getNextWeekday(startDate.format("YYYY-MM-DD"));

      let adjEndDate = getPreviousWeekday(endDate.format("YYYY-MM-DD"));
      if (adjEndDate.format("YYYY-MM-DD") > dayjs().format("YYYY-MM-DD")) {
        adjEndDate = getPreviousWeekday(dayjs().format("YYYY-MM-DD"));
      }

      const newRange: [string, string] = [
        adjStartDate.format("YYYY-MM-DD"),
        adjEndDate.format("YYYY-MM-DD"),
      ];
      setRange(newRange);
      onDateChange?.(newRange);
      return;
    }

    setRange(DEFAULT_RANGE);
    onDateChange?.(DEFAULT_RANGE);
  }, [onDateChange]);

  const formatPreviousRange = useCallback(() => {
    if (!defaultRange?.[0] || !defaultRange?.[1]) return "Pick range";
    return `${dayjs(defaultRange[0]).format("MM/DD/YYYY")} - ${dayjs(defaultRange[1]).format("MM/DD/YYYY")}`;
  }, [defaultRange]);

  const datePicker = selectionMode === DateSelectionMode.BUCKET_INCEPTION ? null : selectionMode === DateSelectionMode.RANGE ? (
    <CustomDatePicker
      range
      format="MM/DD/YYYY"
      onChange={handleRangeChange}
      dateSeparator=" - "
      style={{ height: "56px", width: "200px", fontSize: "16px" }}
      placeholder={formatPreviousRange()}
      showOtherDays
      calendarPosition="top"
      portal
      zIndex={1700}
      aria-label="Select date range"
    />
  ) : (
    <DatePicker
      key={selectionMode}
      views={selectionMode === DateSelectionMode.YEAR ? [DateSelectionMode.YEAR] : [DateSelectionMode.YEAR, DateSelectionMode.MONTH]}
      format={selectionMode === DateSelectionMode.YEAR ? "YYYY" : "MM/YYYY"}
      onAccept={selectionMode === DateSelectionMode.YEAR ? handleYearChange : handleMonthChange}
      minDate={dayjs('2023-01-01')}
      sx={{ height: "56px", width: "200px" }}
      slotProps={{
        textField: {
          placeholder: defaultRange ? defaultRange[0]?.split("-")[0] : `Pick ${selectionMode}`,
          "aria-label": `Select ${selectionMode}`
        },
        popper: {
          sx: { zIndex: 1700 },
        },
      }}
    />
  );

  if (variant === "dropdown") {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 1, paddingY: 1 }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 2 }}>
          <Select
            value={selectionMode}
            onChange={(event) => handleModeChange(event.target.value as DateSelectionMode)}
            renderValue={(mode) => MODE_LABELS[mode]}
            inputProps={{ "aria-label": "date selection mode" }}
            MenuProps={{ sx: { zIndex: 1700 } }}
            sx={{ height: "56px", width: "200px" }}
          >
            <MenuItem value={DateSelectionMode.YEAR}>Year</MenuItem>
            <MenuItem value={DateSelectionMode.MONTH}>Month</MenuItem>
            <MenuItem value={DateSelectionMode.RANGE}>Range</MenuItem>
            {bucketInception && (
              <MenuItem
                value={DateSelectionMode.BUCKET_INCEPTION}
                disabled={!bucketInception.range}
                // Re-picking the already-picked option fires no change, so
                // it re-applies the range here (e.g. after its chip was removed).
                onClick={handleBucketInceptionReclick}
              >
                <Tooltip title={!bucketInception.range ? bucketInception.disabledReason ?? "" : ""} placement="left" describeChild>
                  {/* A disabled item takes no pointer events; this span takes the
                      hover back, and keeps its click from reaching the item. */}
                  <span
                    // Fills the item (MenuItem pads 6px 16px) so its whole row hovers.
                    style={{ pointerEvents: "auto", flex: 1, margin: "-6px -16px", padding: "6px 16px" }}
                    onClick={(event) => {
                      if (!bucketInception.range) event.stopPropagation();
                    }}
                  >
                    {MODE_LABELS[DateSelectionMode.BUCKET_INCEPTION]}
                  </span>
                </Tooltip>
              </MenuItem>
            )}
            {inceptionDate && (
              <MenuItem value={DateSelectionMode.INCEPTION}>Since Inception</MenuItem>
            )}
          </Select>
          {datePicker}
        </Box>
        {additionalComponents}
      </Box>
    );
  }

  return (
    <Grid container sx={{ paddingY: 1, paddingLeft: 1, justifyContent: style ? 'flex-start' : 'flex-end' }} alignItems={style ? 'flex-start' : 'center'}>
      <Grid item marginRight={2}>
        <RadioGroup
          row
          value={selectionMode}
          onChange={(event) => handleModeChange(event.target.value as DateSelectionMode)}
          aria-label="date selection mode"
          sx={style ? { ...style } : {}}
        >
          <FormControlLabel value={DateSelectionMode.YEAR} control={<Radio />} label="Year" />
          <FormControlLabel value={DateSelectionMode.MONTH} control={<Radio />} label="Month" />
          <FormControlLabel value={DateSelectionMode.RANGE} control={<Radio />} label="Range" />
          {bucketInception && (
            <Tooltip
              title={!bucketInception.range ? bucketInception.disabledReason ?? "" : ""}
              placement="left"
            >
              {/* A disabled control fires no mouse events; the span carries the hover. */}
              <span style={{ display: "inline-flex" }}>
                <FormControlLabel
                  value={DateSelectionMode.BUCKET_INCEPTION}
                  // Re-clicking the already-picked option fires no change, so
                  // it re-applies the range here (e.g. after its chip was removed).
                  control={<Radio onClick={handleBucketInceptionReclick} />}
                  disabled={!bucketInception.range}
                  label={bucketInception.range
                    ? `Bucket Inception (${formatRange(bucketInception.range)})`
                    : "Bucket Inception"}
                />
              </span>
            </Tooltip>
          )}
          {inceptionDate && (
            <FormControlLabel value={DateSelectionMode.INCEPTION} control={<Radio />} label="Since Inception" />
          )}
        </RadioGroup>
      </Grid>
      <Grid item marginRight={2}>
        {datePicker}
      </Grid>
      <Grid item>{additionalComponents}</Grid>
    </Grid>
  );
};

export default DateSelector;
